import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { useAccountsAndRoles } from './usersData';
import { ALL_PERMS, GROUPS, PERM_LABEL, customerScope, effectivePerms, groupCodes, scopeBrief, sensitiveFields } from './permissions';
import type { Role } from './types';
import s from './RoleCompare.module.css';
import { PhoneBar } from './kit/Kit';

type Row = { key: string; label: string; left: string | boolean; right: string | boolean; section: string; differs: boolean };

function compareRows(left: Role, right: Role): Row[] {
  const lp = effectivePerms(left);
  const rp = effectivePerms(right);
  const orderLeft = scopeBrief(left, lp).label;
  const orderRight = scopeBrief(right, rp).label;
  const customerLabels = { all: '全部客户', owned: '本人名下', assigned: '分配给我订单的客户' };
  const customersLeft = lp.has('customers.read') ? customerLabels[customerScope(left)] : '不可见';
  const customersRight = rp.has('customers.read') ? customerLabels[customerScope(right)] : '不可见';
  const rows: Row[] = [
    { key: 'scope:orders', label: '查看订单', left: orderLeft, right: orderRight, section: '数据范围', differs: orderLeft !== orderRight },
    { key: 'scope:customers', label: '查看客户', left: customersLeft, right: customersRight, section: '数据范围', differs: customersLeft !== customersRight },
  ];
  const skip = new Set(['orders.read.all', 'orders.read.own', 'orders.read.assigned', 'orders.amount.read']);
  const known = new Set<string>();
  for (const group of GROUPS) {
    for (const code of groupCodes(group)) {
      if (skip.has(code) || known.has(code)) continue;
      known.add(code);
      rows.push({ key: code, label: PERM_LABEL[code] || code, left: lp.has(code), right: rp.has(code), section: group.name, differs: lp.has(code) !== rp.has(code) });
    }
  }
  for (const code of ALL_PERMS) {
    if (skip.has(code) || known.has(code)) continue;
    rows.push({ key: code, label: PERM_LABEL[code] || code, left: lp.has(code), right: rp.has(code), section: '其他功能', differs: lp.has(code) !== rp.has(code) });
  }
  const ls = sensitiveFields(left, lp);
  const rs = sensitiveFields(right, rp);
  ls.forEach((field, index) => rows.push({ key: `sensitive:${index}`, label: field.label, left: field.visible, right: rs[index].visible, section: '敏感字段', differs: field.visible !== rs[index].visible }));
  return rows;
}

function Value({ value }: { value: string | boolean }) {
  return typeof value === 'boolean' ? <span className={value ? s.yes : s.no}>{value ? '✓' : '×'}</span> : <span className={s.scopeValue}>{value}</span>;
}

export function RoleComparePage() {
  const ff = useFormFactor();
  const can = useSession((state) => state.can);
  const data = useAccountsAndRoles();
  const [sp, setSp] = useSearchParams();
  const [onlyDiff, setOnlyDiff] = useState(true);
  const roles = data.roles;
  const left = roles.find((item) => item.id === sp.get('left')) || roles.find((item) => item.code === 'FOLLOWER') || roles[0];
  const right = roles.find((item) => item.id === sp.get('right') && item.id !== left?.id) || roles.find((item) => item.code === 'WAREHOUSE' && item.id !== left?.id) || roles.find((item) => item.id !== left?.id);
  const rows = useMemo(() => left && right ? compareRows(left, right) : [], [left, right]);
  const visible = onlyDiff ? rows.filter((row) => row.differs) : rows;
  const differentCount = rows.filter((row) => row.differs).length;
  const leftOnly = rows.filter((row) => row.left === true && row.right === false).length;
  const rightOnly = rows.filter((row) => row.left === false && row.right === true).length;
  const sameCount = rows.length - differentCount;
  const scopeDiff = rows.filter((row) => row.section === '数据范围' && row.differs).length;
  const sensitiveDiff = rows.filter((row) => row.section === '敏感字段' && row.differs).length;
  const phoneGroups = [
    { key: 'scope', title: '数据范围', rows: visible.filter((row) => row.section === '数据范围'), allRows: rows.filter((row) => row.section === '数据范围') },
    { key: 'features', title: '功能', rows: visible.filter((row) => row.section !== '数据范围' && row.section !== '敏感字段'), allRows: rows.filter((row) => row.section !== '数据范围' && row.section !== '敏感字段') },
    { key: 'sensitive', title: '敏感字段', rows: visible.filter((row) => row.section === '敏感字段'), allRows: rows.filter((row) => row.section === '敏感字段') },
  ];
  const change = (key: 'left' | 'right', value: string) => { const next = new URLSearchParams(sp); next.set(key, value); setSp(next, { replace: true }); };

  if (data.rolesState.error) return <ErrorState title="角色加载失败" message={data.rolesState.error.message} detail="GET /api/roles" onRetry={data.rolesState.reload} />;
  if (data.rolesState.loading && !roles.length) return <div className={s.loading}><Skeleton /><Skeleton lines={5} /></div>;
  if (!left || !right) return <Empty icon="shield" title="至少需要两个角色才能对比">当前没有足够的角色资料。</Empty>;
  const differenceText = left.code === 'FOLLOWER' && right.code === 'WAREHOUSE'
    ? '主要集中在库存写操作 —— 仓库能做的入库出库调整，跟单一项都没有。'
    : `差异涉及${[...new Set(rows.filter((row) => row.differs).map((row) => row.section))].join('、')}等权限。`;
  const explanation = (row: Row) => {
    if (row.section === '数据范围') return '数据范围不同';
    if (row.section === '敏感字段') return '脱敏范围不同';
    if (!row.differs) return '—';
    if (row.left === true && row.right === false) return `仅${left.name}可执行`;
    if (row.left === false && row.right === true) return `仅${right.name}可执行`;
    return '生效权限不同';
  };
  return <div className={`${s.page} ${ff === 'phone' ? s.phone : ff === 'tablet' ? s.tablet : ''}`}>
    {ff === 'phone' && <PhoneBar back="/users?tab=roles" backLabel="角色" title="角色对比" />}
    <header className={s.head}>
      <div className={s.headingGroup}>
        {ff === 'tablet' && <Link className={s.back} to="/users" aria-label="返回角色列表">‹</Link>}
        <div><h1>角色对比{ff === 'tablet' && <span className={s.tabletCount}>共 {differentCount} 处差异</span>}</h1>{ff !== 'tablet' && <p>新建角色最常见的动作是「照着某个角色改一个」，对比只显示差异 <span>共 {differentCount} 处差异</span></p>}</div>
      </div>
      <div className={s.headActions} role="group" aria-label="角色对比操作">
        <button type="button" className={onlyDiff ? s.filterOn : s.filterButton} aria-pressed={onlyDiff} onClick={() => setOnlyDiff((value) => !value)}>只看差异{ff === 'tablet' && <span className={onlyDiff ? s.toggleOn : s.toggle} aria-hidden="true" />}</button>
        {can('roles.create') && <Link className={s.create} to={`/users?createRoleFrom=${encodeURIComponent(right.id)}`}>按右侧新建角色</Link>}
      </div>
    </header>
    <div className={s.scroll}>
      <div className={s.phoneView}>
        <section className={s.phoneControls} aria-label="角色对比条件">
          <div className={s.phoneSelectors}>
            <select aria-label="左侧角色" value={left.id} onChange={(event) => change('left', event.target.value)}>{roles.filter((item) => item.id !== right.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <span>对比</span>
            <select aria-label="右侧角色" value={right.id} onChange={(event) => change('right', event.target.value)}>{roles.filter((item) => item.id !== left.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
          </div>
          <div className={s.phoneFilters} role="group" aria-label="差异筛选">
            <button type="button" className={onlyDiff ? s.phoneFilterOn : s.phoneFilter} aria-pressed={onlyDiff} onClick={() => setOnlyDiff(true)}>只看差异 <b>{differentCount}</b></button>
            <button type="button" className={!onlyDiff ? s.phoneFilterOn : s.phoneFilter} aria-pressed={!onlyDiff} onClick={() => setOnlyDiff(false)}>全部 <b>{rows.length}</b></button>
          </div>
        </section>
        <section className={s.phoneSummary} aria-label="差异摘要">
          <div><span>仅{left.name}有</span><b>{leftOnly}</b></div>
          <div><span>仅{right.name}有</span><b>{rightOnly}</b></div>
          <div><span>范围不同</span><b>{scopeDiff}</b></div>
          <div><span>脱敏不同</span><b>{sensitiveDiff}</b></div>
        </section>
        <div className={s.phoneGroups}>
          {phoneGroups.map((group) => {
            const hiddenSame = group.allRows.length - group.rows.length;
            if (!group.rows.length && onlyDiff) return null;
            return <section className={s.phoneGroup} key={group.key}>
              <header><h2>{group.title}</h2>{hiddenSame > 0 && <span>相同的 {hiddenSame} 项已隐藏</span>}</header>
              <div className={s.phoneTableHead}><span>权限</span><span>{left.name}</span><span>{right.name}</span></div>
              <div className={s.phoneRows}>{group.rows.map((row) => <div className={s.phoneRow} key={row.key}>
                <div className={s.phonePermission}><b>{row.label}</b>{row.differs && <span>{explanation(row)}</span>}{row.section !== '数据范围' && row.section !== '敏感字段' && <small>{row.section}</small>}</div>
                <div className={s.phoneValue}><Value value={row.left} /></div>
                <div className={s.phoneValue}><Value value={row.right} /></div>
              </div>)}</div>
            </section>;
          })}
        </div>
        <p className={s.phoneFoot}><b>{differentCount} 处差异</b>，{differenceText}</p>
        <section className={s.phoneUsage}><h2>常见用法</h2><p>要新建一个「{right.name}主管」，通常是以{right.name}为底，再补充需要的权限。</p></section>
        {can('roles.create') && <Link className={s.phoneCreate} to={`/users?createRoleFrom=${encodeURIComponent(right.id)}`}>以「{right.name}」为底新建角色</Link>}
        <p className={s.phoneNote}>功能权限按“授予减去限制项”后的生效结果计算；此页只读取 V1 共用角色数据。</p>
      </div>
      <div className={s.dwrap}>
        <main className={s.dmain}>
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2>选择两个角色</h2>
              <div className={s.selectors}>
                <select aria-label="左侧角色" value={left.id} onChange={(event) => change('left', event.target.value)}>{roles.filter((item) => item.id !== right.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
                <span>对比</span>
                <select aria-label="右侧角色" value={right.id} onChange={(event) => change('right', event.target.value)}>{roles.filter((item) => item.id !== left.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
              </div>
            </div>
            {visible.length ? <div className={s.tableScroll}><table className={s.cmp}>
              <thead><tr><th>权限</th><th>{left.name}</th><th>{right.name}</th><th>说明</th></tr></thead>
              <tbody>{visible.map((row) => <tr className={row.differs ? s.diff : s.same} key={row.key}><td>{row.label}</td><td className={s.valueCell}><Value value={row.left} /></td><td className={s.valueCell}><Value value={row.right} /></td><td className={s.explanation}>{explanation(row)}</td></tr>)}</tbody>
            </table></div> : <Empty icon="ok" title="两个角色没有差异">切换到“全部”可查看共同权限。</Empty>}
            <p className={s.tableFoot}><b>{differentCount} 处差异</b>，{differenceText}</p>
          </section>
        </main>
        <aside className={s.daside}>
          <section className={s.card}><div className={s.cardHead}><h2>差异摘要</h2></div><div className={s.cardBody}>
            <div><span>相同</span><b>{sameCount} 项</b></div><div><span>仅{left.name}有</span><b>{leftOnly} 项</b></div><div><span>仅{right.name}有</span><b>{rightOnly} 项</b></div><div><span>范围不同</span><b>{scopeDiff} 项</b></div><div><span>脱敏不同</span><b>{sensitiveDiff} 项</b></div>
          </div></section>
          <section className={s.card}><div className={s.cardHead}><h2>常见用法</h2></div><div className={s.cardBody}><p>要新建一个「{right.name}主管」，通常是<b>以{right.name}为底</b>，再补充需要的权限。<br /><br />点右上角「按右侧新建角色」会把{right.name}的全部权限复制成新角色的初始值。</p></div></section>
          <p className={s.note}>功能权限按“授予减去限制项”后的生效结果计算；此页只读取 V1 共用角色数据。</p>
        </aside>
      </div>
    </div>
  </div>;
}
